// The canvas renders storefront blocks, and those blocks translate through `t()`. `t()` reads the bundled
// dictionaries through a registry that the SERVER fills — the storefront's browser bundle deliberately
// carries French alone, because a shop's page inlines its own language (see `@aquilixshop/i18n/bundled`).
// The builder is not a shop page: it renders a store's blocks in the seller's browser with no page block to
// read from, so it must register that store's dictionary itself, or an Arabic or Spanish store would edit
// its blocks in French.
//
// That registration is NOT an import here. It used to be, and a static import of all twenty-six put 918 KB
// of dictionaries in the entry chunk of an editor that opens ONE store in ONE language. `loadStore()` now
// awaits `ensureStoreDictionary(language)` the moment it learns which language that is, and pays for that
// one — see `@aquilixshop/ui-react/bundled`.
import { formatMoney as sharedFormatMoney } from '@aquilixshop/money';
import type {
  Direction,
  ProductActiveOffer,
  RenderContext,
  StoreLocale,
  ThemeSettings,
} from '@aquilixshop/ui-react';
import { DEFAULT_THEME_SETTINGS } from '@aquilixshop/ui-react';
import { type CodConfig, DEFAULT_COD_CONFIG } from '@aquilixshop/cod-config';

/**
 * Build the edit-mode `RenderContext` the canvas renders against (build-plan §4.2, §9.3).
 *
 * `renderMode: 'edit'` is the seam that tells the renderer it is in the builder (disabled sections
 * stay visible, etc.). The context is built ONCE on load and stored as a stable reference in the
 * Zustand store — never reconstructed inline in render, or every section would re-render (§9.3).
 *
 * The M2 `BuilderPageResponse` returns only `{document, version, ...}` — no store/theme metadata — so
 * M3.1 derives `dir` from the document locale and uses the default theme tokens. The real
 * per-store theme (the M5 theme-settings editor) plugs in here later by passing resolved
 * `themeSettings`; the canvas wiring is unchanged.
 */
export function buildEditContext(
  locale: StoreLocale,
  page: RenderContext['page'] = { routeType: 'home' },
  // 0019 finding #4: the load path threads the store's resolved theme here; default = the v2 Shrine theme
  // (was a hardcoded v1 indigo/cream lean-5 blob that no longer matches the storefront token model).
  theme: ThemeSettings = DEFAULT_THEME_SETTINGS,
  // feature 0026: the seller's COD app config (GET /api/store/apps/cod/config), so the canvas's buy-box
  // `cod-form` block renders the SAME fields / theme / button the seller designed in the dashboard — the
  // builder preview matches the live storefront. Default = DEFAULT_COD_CONFIG (the preview is never empty).
  codConfig: CodConfig = DEFAULT_COD_CONFIG,
  // feature 0026-06: the store's real selling country (ISO-3166 a2) + currency, loaded once from
  // GET /api/store/settings/general. They drive the `cod-form` preview's phone example / dial prefix — so
  // the builder preview matches the live storefront instead of assuming Morocco. Empty until loaded.
  // (The city field is plain free text — nothing country-dependent about it.)
  storeCountry = '',
  storeCurrency = 'MAD',
  // feature 0026-07: the bound preview product's active per-product quantity offer (fetched store-side via
  // GET /api/quantity-offers?productId=…, then mapped to the render shape). Threading it here lets the buy-box
  // `quantity` block preview the seller's Releasit-style offer TILES instead of the classic stepper — so a
  // seller editing a product that HAS an offer sees the offer in the builder, matching the live storefront.
  // Absent (undefined) when the product has no offer / the fetch failed → the block falls back to the stepper
  // (fail-open). The block only shows the tiles when `activeOffer.productId === the bound product's id`.
  activeOffer?: ProductActiveOffer,
  // The ONE method the store accepts (`GET /api/store/payment-mode`, loaded once by `loadStore`). This is the
  // RESOLVED method, not a capability: a store with Stripe connected that sells cash-on-delivery is a COD
  // store on this canvas. `undefined` = not known yet (the fetch is concurrent with the first paint, and may
  // fail) — the buy-box then falls back to its own default rather than asserting a method. Once known it is
  // what makes the preview honest: the canvas shows the buy-box the customer really gets.
  paymentMethod?: 'CARD' | 'COD',
): RenderContext {
  const dir: Direction = locale === 'ar' ? 'rtl' : 'ltr';
  const enabledPaymentMethods: readonly ('COD' | 'CARD')[] | undefined =
    paymentMethod === undefined ? undefined : [paymentMethod];
  return {
    store: {
      id: 'store',
      slug: 'store',
      name: 'Store',
      currency: storeCurrency,
      countryCode: storeCountry,
    },
    locale,
    dir,
    themeSettings: theme,
    codConfig,
    enabledPaymentMethods,
    // The checkout PARADIGM the method implies — the same derivation the backend serves the storefront
    // (`StorefrontPageQueryService.checkoutStyleFor`): a card store checks out through the cart, a COD store
    // through the single-page funnel. The header reads it to keep the cart icon on a cart store, so the
    // canvas has to know it too or the builder would show a header the storefront never renders.
    checkoutStyle:
      paymentMethod === undefined ? undefined : paymentMethod === 'CARD' ? 'cart' : 'funnel',
    activeOffer,
    page,
    // When previewing a product template, also expose the bound product by id so product-picker block
    // fields that reference "this product" resolve (feature 0018 M2).
    resolved: page.routeType === 'product' ? { products: { [page.product.id]: page.product } } : {},
    renderMode: 'edit',
    // Feature 0049 lot D: ISO exponent via @aquilixshop/money, not a hard-coded 100 — so the canvas shows a
    // KWD or XOF price the way the live storefront will, which is the whole point of the preview.
    formatMoney: (m) => sharedFormatMoney(m, locale),
    resolveUrl: (target) => {
      switch (target.type) {
        case 'external':
          return target.url || '#';
        case 'product':
          return target.slug ? `/products/${target.slug}` : '#';
        case 'collection':
          return target.slug ? `/collections/${target.slug}` : '#';
        case 'page':
          return `/pages/${target.slug}`;
        case 'catalog':
          // The ALL-PRODUCTS catalog page (feature 0027 M2) — a fixed target. Menus link to it by type, so
          // omitting it here made every "Catalogue" nav entry render as a dead `#` in the editor only.
          return '/collections/all';
        case 'policy':
          // One of the six legal pages (/policies/<slug>) — same reason as the catalog above: a menu links
          // to it by type, so without this case the entry is a dead `#` in the editor.
          return target.slug ? `/policies/${target.slug}` : '#';
        default:
          return '#';
      }
    },
  };
}
